import { type FC } from 'react';
import cx from 'classnames';
import { UnstyledButton } from '../UnstyledButton';
import { ComingSoonBadge } from '../ComingSoonBadge';
import { PersonSimpleRunIcon } from '../SceneBannerCard/icons';
import styles from './HistoricalSceneCard.module.css';

type Props = {
  title: string;
  subtitle: string;
  /** Square preview image. Omitted → neutral placeholder (stub state). */
  preview?: string;
  exploreLabel: string;
  /** This card represents the scene the user is currently in → CTA disabled. */
  isCurrent: boolean;
  /** The era's scene isn't built yet → muted card, "Coming soon" badge, no CTA. */
  isLocked: boolean;
  comingSoonLabel: string;
  onExplore: () => void;
};

/**
 * One slide of the historical-scenes slider. Fixed light surface (white card)
 * per design — internal colors are intentionally NOT theme-adaptive (a white
 * card with `--color-fg-primary` text would be invisible in dark theme). See
 * `HistoricalSceneCard.module.css`.
 *
 * The "Explore" CTA is disabled when `isCurrent` — you are already in this
 * scene (the slider centers on it by default). `onPointerDown preventDefault`
 * keeps the chat input from losing focus on mobile (shared chat-button rule).
 *
 * `isLocked` is the design's "not built yet" state (node `3060:12606`): the
 * preview + text drop to 50 % opacity, a gold "Coming soon" badge floats over
 * the preview, and the CTA is replaced by an empty slot of the same height so
 * locked and unlocked cards stay the same size in the carousel.
 */
export const HistoricalSceneCard: FC<Props> = ({
  title,
  subtitle,
  preview,
  exploreLabel,
  isCurrent,
  isLocked,
  comingSoonLabel,
  onExplore,
}) => (
  <div className={styles.card}>
    {preview ? (
      <img
        src={preview}
        alt=""
        className={cx(styles.preview, isLocked && styles.dimmed)}
        draggable={false}
      />
    ) : (
      <div className={cx(styles.preview, isLocked && styles.dimmed)} aria-hidden="true" />
    )}
    {isLocked && <ComingSoonBadge label={comingSoonLabel} className={styles.badge} />}
    <div className={cx(styles.body, isLocked && styles.dimmed)}>
      <div className={styles.text}>
        {/* No `dir="auto"`: i18n copy always matches the document direction,
            and digit-only era titles ("605" / "1890") have no strong character
            for `auto` to read — it resolves LTR (harmless while the card is
            centered, a left-alignment bug the moment it isn't; see the same
            note in `MenuSceneCard`). */}
        <p className={styles.title}>{title}</p>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>
      {isLocked ? (
        <span className={styles.ctaSlot} aria-hidden="true" />
      ) : (
        <UnstyledButton
          className={cx(styles.cta, isCurrent && styles.ctaDisabled)}
          disabled={isCurrent}
          aria-disabled={isCurrent}
          onPointerDown={(e) => e.preventDefault()}
          onClick={isCurrent ? undefined : onExplore}
        >
          <span className={styles.ctaIcon}>
            <PersonSimpleRunIcon />
          </span>
          <span className={styles.ctaLabel}>{exploreLabel}</span>
        </UnstyledButton>
      )}
    </div>
  </div>
);
